import { useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { selectData, selectById, selectIsLoading } from '../selectors.ts';
import { useAttach } from '../useAttach';

import { flowFoldersDraftStore } from './FlowFoldersDraftStore.ts';
import { flowFoldersStore, type FlowFoldersStoreState } from './FlowFoldersStore.ts';

export function useFlowFoldersData() {
  useAttach(flowFoldersStore, 'useFlowFoldersData');
  return useStoreWithEqualityFn(flowFoldersStore.store, selectData, shallow);
}
useFlowFoldersData.store = flowFoldersStore;

export function useFlowFoldersById() {
  useAttach(flowFoldersStore, 'useFlowFoldersById');

  const draftState = flowFoldersDraftStore.store((state) => {
    return {
      flowFolders: state.flowFolders,
    };
  }, shallow);

  const storeState = useStoreWithEqualityFn(flowFoldersStore.store, selectById, shallow);

  const state = useMemo(() => {
    if (draftState == null) {
      return storeState;
    }

    return {
      ...storeState,
      byId: {
        ...storeState.byId,
        ...draftState.flowFolders,
      },
    };
  }, [storeState, draftState]);

  return state;
}
useFlowFoldersById.store = flowFoldersStore;

export function useFlowFolder({ flowFolderId }: { flowFolderId: string }) {
  useAttach(flowFoldersStore, 'useFlowFolder');

  const select = useCallback(
    (state: FlowFoldersStoreState) => {
      return {
        flowFolder: state.byId?.[flowFolderId] ?? null,
        flowFolderInstance: state.data?.[flowFolderId] ?? null,
        isLoading: state.isLoading,
        error: state.error,
      };
    },
    [flowFolderId],
  );

  return useStoreWithEqualityFn(flowFoldersStore.store, select, shallow);
}
useFlowFolder.store = flowFoldersStore;

export function useFlowFoldersAttach() {
  useAttach(flowFoldersStore, 'useFlowFoldersAttach');
}
useFlowFoldersAttach.store = flowFoldersStore;

export function useFlowFoldersLoading() {
  useAttach(flowFoldersStore, 'useFlowFoldersLoading');
  return useStoreWithEqualityFn(flowFoldersStore.store, selectIsLoading, shallow);
}
useFlowFoldersLoading.store = flowFoldersStore;
